import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Banner,
  Button,
  EmptyState,
  Icon,
  IconButton,
  Select,
  Sheet,
  Spinner,
  Tag,
  TextArea,
  useCkToast,
} from "@NP-Webber/crm-kit/dist/next/index.js";
import {
  aiApi,
  aiErrorText,
  type AiActionDto,
  type AiClientActionDto,
  type AiConversationSummaryDto,
  type AiDisplayDto,
  type AiSavedReportDto,
} from "../../api/aiApi";
import { AiCreditChip } from "../ai/credits/AiCreditChip";
import { AiFeedbackButtons } from "../ai/feedback/AiFeedbackButtons";
import { EVENTS, notifyDataChanged, onEvent, openAutomations, openHelpCenter } from "../events";
import { runQuickCreate, type QuickCreateType } from "../quickCreate";
import { HUB_SECTIONS } from "../settings/settingsSections";
import { useShell } from "../shell/ShellContext";
import { AssistantArtifacts } from "./AssistantArtifacts";
import "./assistant.css";

/**
 * עוזר AI (ADR-0030, ADR-0035) — Turbo's side assistant: the "AI" badge in the top bar (Ctrl+J) opens a
 * sheet that talks to POST /ai/chat. Reads go through the user's own permissions; tables / KPI cards
 * show under the answer; writes come back as approval cards; screens, forms, settings and guides
 * come back as buttons; 👍 / 👎 per answer; tables can be saved as reports and run again.
 */
type Item =
  | { kind: "user"; text: string }
  | {
      kind: "assistant";
      text: string;
      steps?: string[];
      clientActions?: AiClientActionDto[];
      displays?: AiDisplayDto[];
      ref?: string | null;
    }
  | { kind: "error"; text: string };

const SUGGESTIONS = [
  "מה מחכה לי היום?",
  "מי הלקוחות עם החוב הגבוה ביותר?",
  "אילו הרשאות לחיוב פגות החודש?",
  "תכין לי משימה לחזור ללקוח מחר",
  "איך מוסיפים אוטומציה?",
  "תבדוק אם יש בעיות בנתונים",
];

const ACTION_UI: Record<string, { icon: string; approve: string; done: string }> = {
  create_task: { icon: "task", approve: "אישור ויצירה", done: "המשימה נוצרה" },
  create_customer: { icon: "user", approve: "אישור ופתיחה", done: "הלקוח נפתח" },
  update_customer: { icon: "edit", approve: "אישור ועדכון", done: "הכרטיס עודכן" },
  create_income: { icon: "coin", approve: "אישור ורישום", done: "ההכנסה נרשמה" },
  create_payment: { icon: "card", approve: "אישור ורישום", done: "התשלום נרשם" },
  send_email: { icon: "mail", approve: "אישור ושליחה", done: "המייל נשלח" },
  create_automation: { icon: "sparkle", approve: "אישור והפעלה", done: "האוטומציה הופעלה" },
};
const actionUi = (kind: string) => ACTION_UI[kind] ?? { icon: "check", approve: "אישור", done: "בוצע" };

const MODEL_KEY = "tb.ai.model.assistant";
const readModel = () => {
  try {
    return window.localStorage.getItem(MODEL_KEY);
  } catch {
    return null;
  }
};
const writeModel = (v: string) => {
  try {
    window.localStorage.setItem(MODEL_KEY, v);
  } catch {
    /* private mode */
  }
};

let statusPromise: Promise<boolean> | null = null;
/** true when the server has an AI provider (asked once per page load) */
export function loadAssistantConfigured(): Promise<boolean> {
  if (!statusPromise) {
    statusPromise = aiApi
      .status()
      .then((s) => s.configured)
      .catch(() => false);
  }
  return statusPromise;
}

function useAssistantAvailable(): boolean {
  const { allowed } = useShell();
  const may = allowed("ai.use");
  const [configured, setConfigured] = useState(false);
  useEffect(() => {
    if (!may) return;
    let alive = true;
    void loadAssistantConfigured().then((v) => alive && setConfigured(v));
    return () => {
      alive = false;
    };
  }, [may]);
  return may && configured;
}

/** **bold** and "- " bullets — enough for the assistant's short answers */
function renderText(text: string): ReactNode {
  const inline = (line: string) =>
    line.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
      part.startsWith("**") && part.endsWith("**") ? <b key={i}>{part.slice(2, -2)}</b> : <Fragment key={i}>{part}</Fragment>,
    );
  const lines = text.split("\n");
  const out: ReactNode[] = [];
  let bullets: string[] = [];
  let ordered = false;
  const flush = () => {
    if (bullets.length) {
      const items = bullets.map((b, i) => <li key={i}>{inline(b)}</li>);
      out.push(ordered ? <ol key={`ol-${out.length}`}>{items}</ol> : <ul key={`ul-${out.length}`}>{items}</ul>);
      bullets = [];
    }
  };
  lines.forEach((line, i) => {
    const m = /^\s*([-*•]|\d+[.)])\s+(.*)$/.exec(line);
    if (m) {
      const isOrdered = /\d/.test(m[1]!);
      if (bullets.length && isOrdered !== ordered) flush();
      ordered = isOrdered;
      bullets.push(m[2]!);
      return;
    }
    flush();
    if (line.trim()) out.push(<p key={`p-${i}`}>{inline(line)}</p>);
  });
  flush();
  return out;
}

/** "AI" — in the top bar's end slot; hidden when the user lacks ai.use or no provider is configured. */
export function AssistantButton() {
  const available = useAssistantAvailable();
  useEffect(() => {
    if (!available) return undefined;
    const onKey = (e: globalThis.KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === "j" || e.key === "J")) {
        e.preventDefault();
        window.dispatchEvent(new CustomEvent(EVENTS.assistant, { detail: { prompt: null } }));
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [available]);
  if (!available) return null;
  return (
    <button
      type="button"
      className="ai-btn"
      onClick={() => window.dispatchEvent(new CustomEvent(EVENTS.assistant, { detail: { prompt: null } }))}
      aria-label="עוזר AI (Ctrl+J)"
      title="עוזר AI (Ctrl+J)"
    >
      AI
    </button>
  );
}

export function AssistantPanel() {
  const available = useAssistantAvailable();
  const { current, can, allowed } = useShell();
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useCkToast();
  const [open, setOpen] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [items, setItems] = useState<Item[]>([]);
  const [actions, setActions] = useState<AiActionDto[]>([]);
  const [actionPaths, setActionPaths] = useState<Record<string, string | undefined>>({});
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState<"chat" | "history" | "reports">("chat");
  const [history, setHistory] = useState<AiConversationSummaryDto[] | null>(null);
  const [reports, setReports] = useState<AiSavedReportDto[] | null>(null);
  const [votes, setVotes] = useState<Record<string, "up" | "down">>({});
  const [deciding, setDeciding] = useState<string | null>(null);
  const [models, setModels] = useState<Array<{ id: string; label: string }>>([]);
  const [modelId, setModelId] = useState<string | null>(readModel);
  const [turns, setTurns] = useState(0);
  const bodyRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  /** settings sections this user can open — the assistant may only open one of these */
  const settings = useMemo(
    () =>
      HUB_SECTIONS.filter((s) => (!s.nav || can(s.nav)) && (!s.perm || s.perm.some(allowed))).map((s) => ({ key: s.key, title: s.title })),
    [can, allowed],
  );

  useEffect(() => {
    if (!available || !open) return;
    let alive = true;
    aiApi
      .models()
      .then((m) => alive && setModels(m.choices))
      .catch(() => alive && setModels([]));
    return () => {
      alive = false;
    };
  }, [available, open]);

  const scrollDown = () =>
    window.requestAnimationFrame(() => bodyRef.current?.scrollTo({ top: bodyRef.current.scrollHeight, behavior: "smooth" }));

  const send = useCallback(
    async (text: string) => {
      const message = text.trim();
      if (!message || busy) return;
      setDraft("");
      setView("chat");
      setItems((prev) => [...prev, { kind: "user", text: message }]);
      setBusy(true);
      scrollDown();
      try {
        const res = await aiApi.chat({
          conversationId,
          message,
          modelId: models.some((m) => m.id === modelId) ? modelId : null,
          screen: { path: `${location.pathname}${location.search}`, title: current?.label ?? null },
          context: { settings },
        });
        setConversationId(res.conversationId);
        setItems((prev) => [
          ...prev,
          {
            kind: "assistant",
            text: res.reply,
            steps: res.stepLabels,
            clientActions: res.clientActions,
            displays: res.displays,
            ref: res.ref,
          },
        ]);
        if (res.actions.length) setActions((prev) => [...prev, ...res.actions]);
      } catch (e) {
        setItems((prev) => [...prev, { kind: "error", text: aiErrorText(e) }]);
      } finally {
        setBusy(false);
        setTurns((n) => n + 1);
        scrollDown();
        inputRef.current?.focus();
      }
    },
    [busy, conversationId, current?.label, location.pathname, location.search, modelId, models, settings],
  );

  useEffect(
    () =>
      onEvent<{ prompt: string | null } | undefined>(EVENTS.assistant, (detail) => {
        setOpen((was) => (detail?.prompt ? true : !was));
        if (detail?.prompt) void send(detail.prompt);
        window.setTimeout(() => inputRef.current?.focus(), 60);
      }),
    [send],
  );

  if (!available) return null;

  const reset = () => {
    setConversationId(null);
    setItems([]);
    setActions([]);
    setActionPaths({});
    setView("chat");
    inputRef.current?.focus();
  };

  const toggleHistory = async () => {
    if (view === "history") {
      setView("chat");
      return;
    }
    try {
      setHistory(await aiApi.conversations());
      setView("history");
    } catch (e) {
      toast.error((e as { message?: string })?.message || "לא הצלחנו לטעון שיחות קודמות");
    }
  };

  const toggleReports = async () => {
    if (view === "reports") {
      setView("chat");
      return;
    }
    try {
      setReports(await aiApi.reports());
      setView("reports");
    } catch (e) {
      toast.error((e as { message?: string })?.message || "לא הצלחנו לטעון דוחות שמורים");
    }
  };

  const openConversation = async (id: string) => {
    try {
      const [conv, mine] = await Promise.all([aiApi.conversation(id), aiApi.myFeedback("assistant").catch(() => [])]);
      setConversationId(conv.id);
      setVotes(Object.fromEntries(mine.map((f) => [f.refKey, f.rating])));
      setItems(
        conv.items.map((i) =>
          i.role === "user"
            ? { kind: "user", text: i.text }
            : { kind: "assistant", text: i.text, steps: i.steps, ref: i.ref, displays: i.displays, clientActions: i.clientActions },
        ),
      );
      setActions(conv.actions);
      setView("chat");
      scrollDown();
    } catch (e) {
      toast.error((e as { message?: string })?.message || "לא הצלחנו לפתוח את השיחה");
    }
  };

  const removeConversation = async (id: string) => {
    try {
      await aiApi.deleteConversation(id);
      setHistory((h) => (h ? h.filter((c) => c.id !== id) : h));
      if (id === conversationId) reset();
    } catch (e) {
      toast.error((e as { message?: string })?.message || "המחיקה נכשלה");
    }
  };

  const saveReport = async (display: Extract<AiDisplayDto, { kind: "table" }>) => {
    if (!display.source) return;
    try {
      await aiApi.saveReport({ title: display.title, tool: display.source.tool, input: display.source.input });
      toast.success("נשמר בדוחות השמורים");
    } catch (e) {
      toast.error((e as { message?: string })?.message || "השמירה נכשלה");
    }
  };

  const runReport = async (r: AiSavedReportDto) => {
    setView("chat");
    setBusy(true);
    try {
      const res = await aiApi.runReport(r.id);
      setItems((prev) => [...prev, { kind: "assistant", text: `הדוח «${res.title}» — נכון לעכשיו:`, displays: [res.display] }]);
    } catch (e) {
      setItems((prev) => [...prev, { kind: "error", text: aiErrorText(e) }]);
    } finally {
      setBusy(false);
      scrollDown();
    }
  };

  const removeReport = async (id: string) => {
    try {
      await aiApi.deleteReport(id);
      setReports((list) => (list ? list.filter((r) => r.id !== id) : list));
    } catch (e) {
      toast.error((e as { message?: string })?.message || "המחיקה נכשלה");
    }
  };

  const decide = async (action: AiActionDto, decision: "approve" | "reject") => {
    setDeciding(action.id);
    try {
      const res = await aiApi.decide(action.id, decision);
      setActions((prev) => prev.map((a) => (a.id === action.id ? { ...a, status: res.status } : a)));
      if (res.status === "executed") {
        toast.success(actionUi(action.kind).done);
        if (res.entity) notifyDataChanged(res.entity);
        setActionPaths((p) => ({ ...p, [action.id]: res.path }));
      }
    } catch (e) {
      setActions((prev) => prev.map((a) => (a.id === action.id ? { ...a, status: "failed" } : a)));
      toast.error(aiErrorText(e));
    } finally {
      setDeciding(null);
      setTurns((n) => n + 1);
    }
  };

  /** the browser side of a tool: always a button — nothing opens by itself */
  const runClientAction = (a: AiClientActionDto) => {
    if (a.type === "navigate") {
      setOpen(false);
      navigate(a.path);
    } else if (a.type === "quick_create") {
      setOpen(false);
      runQuickCreate(a.create as QuickCreateType, (to, opts) => navigate(to, opts));
    } else if (a.type === "open_setting") {
      setOpen(false);
      navigate(`/settings?section=${encodeURIComponent(a.key)}`);
    } else if (a.type === "open_help") {
      openHelpCenter(a.slug);
    } else if (a.type === "open_automations") {
      setOpen(false);
      openAutomations({ tab: a.tab, prompt: a.prompt });
    }
  };
  const clientActionLabel = (a: AiClientActionDto) =>
    a.type === "navigate"
      ? `מעבר ל${a.label}`
      : a.type === "quick_create"
        ? a.label
        : a.type === "open_setting"
          ? `פתיחת ההגדרה «${a.label}»`
          : a.type === "open_help"
            ? `המדריך «${a.label}»`
            : a.tab === "ai"
              ? "יצירת האוטומציה עם AI"
              : "פתיחת האוטומציות";

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void send(draft);
    }
  };

  const pending = actions.filter((a) => a.status === "pending");
  const decided = actions.filter((a) => a.status !== "pending");
  const fmtWhen = (iso: string) =>
    new Date(iso).toLocaleString("he-IL", { day: "numeric", month: "numeric", hour: "2-digit", minute: "2-digit" });

  return (
    <Sheet open={open} onClose={() => setOpen(false)} width={500} ariaLabel="עוזר AI" className="ai">
      <header className="ai__head">
        <span className="ai__logo">AI</span>
        <div className="ai__titles">
          <b>עוזר AI</b>
          <small>{current?.label ? `במסך: ${current.label}` : "שואל, מסכם ומכין פעולות לאישורך"}</small>
        </div>
        <AiCreditChip refreshKey={turns} />
        <IconButton icon="chart" label="דוחות שמורים" active={view === "reports"} onClick={() => void toggleReports()} />
        <IconButton icon="history" label="שיחות קודמות" active={view === "history"} onClick={() => void toggleHistory()} />
        <IconButton icon="plus" label="שיחה חדשה" onClick={reset} />
        <IconButton icon="x" label="סגירה" onClick={() => setOpen(false)} />
      </header>

      <div className="ai__body" ref={bodyRef}>
        {view === "history" ? (
          <div className="ai__history">
            {!history?.length ? <EmptyState icon="history" title="אין עדיין שיחות" /> : null}
            {(history ?? []).map((c) => (
              <div key={c.id} className="ai__history-row">
                <button type="button" onClick={() => void openConversation(c.id)}>
                  <span>{c.title}</span>
                  <small>{fmtWhen(c.updatedAt)}</small>
                </button>
                <IconButton icon="trash" size="sm" label="מחיקת השיחה" onClick={() => void removeConversation(c.id)} />
              </div>
            ))}
          </div>
        ) : view === "reports" ? (
          <div className="ai__history">
            {!reports?.length ? (
              <EmptyState icon="chart" title="אין דוחות שמורים">
                טבלה שהעוזר מציג אפשר לשמור (📌) ולהריץ שוב בכל רגע, עם הנתונים העדכניים.
              </EmptyState>
            ) : null}
            {(reports ?? []).map((r) => (
              <div key={r.id} className="ai__history-row">
                <button type="button" onClick={() => void runReport(r)}>
                  <span>{r.title}</span>
                  <small>נשמר {fmtWhen(r.createdAt)} · לחיצה מריצה מחדש</small>
                </button>
                <IconButton icon="trash" size="sm" label="מחיקת הדוח" onClick={() => void removeReport(r.id)} />
              </div>
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="ai__welcome">
            <p>
              אפשר לשאול על לקוחות, גבייה, הכנסות, משימות, ישיבות ואוטומציות, ולבקש שאכין פעולה — אני רואה רק מה שמותר לך לראות, וכל שינוי
              עובר דרכך לאישור.
            </p>
            <div className="ai__suggest">
              {SUGGESTIONS.map((s) => (
                <button key={s} type="button" onClick={() => void send(s)}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="ai__thread">
            {items.map((it, i) =>
              it.kind === "user" ? (
                <div key={i} className="ai__msg ai__msg--user">
                  {it.text}
                </div>
              ) : it.kind === "error" ? (
                <Banner key={i} tone="danger">
                  {it.text}
                </Banner>
              ) : (
                <div key={i} className="ai__msg ai__msg--bot">
                  {it.steps?.length ? (
                    <div className="ai__steps">
                      <Icon name="sparkle" size={13} />
                      {it.steps.join(" · ")}
                    </div>
                  ) : null}
                  {it.text ? <div className="ai__text">{renderText(it.text)}</div> : null}
                  {it.displays?.length ? <AssistantArtifacts displays={it.displays} onSave={(d) => void saveReport(d)} /> : null}
                  {it.clientActions?.length ? (
                    <div className="ai__cta">
                      {it.clientActions.map((a, k) => (
                        <Button key={k} size="sm" icon={a.type === "open_help" ? "book" : "left"} onClick={() => runClientAction(a)}>
                          {clientActionLabel(a)}
                        </Button>
                      ))}
                    </div>
                  ) : null}
                  {it.ref ? <AiFeedbackButtons key={it.ref} surface="assistant" refKey={it.ref} initial={votes[it.ref] ?? null} /> : null}
                </div>
              ),
            )}
            {busy ? (
              <div className="ai__msg ai__msg--bot ai__typing">
                <Spinner size={16} /> חושב…
              </div>
            ) : null}
          </div>
        )}

        {view === "chat" && (pending.length || decided.length) ? (
          <div className="ai__actions">
            {pending.map((a) => (
              <div key={a.id} className="ai__card">
                <div className="ai__card-top">
                  <Icon name={actionUi(a.kind).icon} size={16} />
                  <span>{a.summary}</span>
                </div>
                <div className="ai__card-btns">
                  <Button size="sm" variant="primary" disabled={deciding === a.id} onClick={() => void decide(a, "approve")}>
                    {actionUi(a.kind).approve}
                  </Button>
                  <Button size="sm" variant="ghost" disabled={deciding === a.id} onClick={() => void decide(a, "reject")}>
                    לא עכשיו
                  </Button>
                </div>
              </div>
            ))}
            {decided.map((a) => (
              <div key={a.id} className="ai__card ai__card--done">
                <span>{a.summary}</span>
                <span className="ai__card-end">
                  {a.status === "executed" && actionPaths[a.id] ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setOpen(false);
                        navigate(actionPaths[a.id]!);
                      }}
                    >
                      פתיחה
                    </Button>
                  ) : null}
                  <Tag tone={a.status === "executed" ? "success" : a.status === "rejected" ? "neutral" : "danger"}>
                    {a.status === "executed" ? "בוצע" : a.status === "rejected" ? "נדחה" : "נכשל"}
                  </Tag>
                </span>
              </div>
            ))}
          </div>
        ) : null}
      </div>

      <footer className="ai__foot">
        {models.length > 1 ? (
          <Select
            aria-label="מודל"
            className="ai__model"
            value={models.some((m) => m.id === modelId) ? modelId : ""}
            options={[{ value: "", label: "מודל ברירת מחדל" }, ...models.map((m) => ({ value: m.id, label: m.label }))]}
            onChange={(v) => {
              setModelId(v || null);
              writeModel(v);
            }}
          />
        ) : null}
        <div className="ai__compose">
          <TextArea
            ref={inputRef}
            rows={2}
            value={draft}
            maxLength={4000}
            placeholder="מה לבדוק או להכין? (Enter לשליחה)"
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKeyDown}
            disabled={busy}
          />
          <Button variant="primary" icon="left" iconOnly aria-label="שליחה" disabled={busy || !draft.trim()} onClick={() => void send(draft)} />
        </div>
      </footer>
    </Sheet>
  );
}
